<script setup lang="ts">
import { computed } from "vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import ArrowIcon from "@magnit/icons/src/assets/icons/arrow-left-corner-16.svg";
import type { ICategoriesFunctions } from "../types";

const props = defineProps<{
  categoryKey: string;
  categoriesStore: ICategoriesFunctions;
}>();

const currentCategory = computed(() =>
  props.categoriesStore.getCategoryByKey(props.categoryKey),
);

const parentsCategories = computed(() => {
  let parents = props.categoriesStore.getParentsByKey(props.categoryKey);
  const type = props.categoriesStore.getTypeByKey(props.categoryKey);

  const base = props.categoriesStore.getCategoryById(
    "",
    type === "promo" ? "goods" : "promo",
  );

  if (base) {
    parents = [base, ...parents];
  }

  if (currentCategory.value?.children.length) {
    parents.push(currentCategory.value);
  }

  return parents;
});

const childrenCategories = computed(() => {
  if (currentCategory.value?.children.length) {
    return currentCategory.value.children;
  }

  if (currentCategory.value?.parentKey) {
    const parent = props.categoriesStore.getCategoryByKey(
      currentCategory.value.parentKey,
    );

    if (parent) {
      return parent.children;
    }
  }

  return [];
});
</script>

<template>
  <div class="filters-category">
    <VText tag="div" font="title-large"> Каталог товаров </VText>
    <div class="filters-category__container">
      <div class="filters-category__parents">
        <NuxtLink
          v-for="parent in parentsCategories"
          :key="parent.key"
          :to="parent.url"
          class="filters-category__item"
          :class="{
            'filters-category__item_selected': parent.key === categoryKey,
          }"
        >
          <VIcon class="filters-category__item__arrow">
            <ArrowIcon />
          </VIcon>

          <span class="filters-category__item__text">
            {{ parent.name }}
          </span>
        </NuxtLink>
      </div>
      <div class="filters-category__children">
        <NuxtLink
          v-for="child in childrenCategories"
          :key="child.key"
          :to="child.url"
          class="filters-category__item"
          :class="{
            'filters-category__item_selected': child.key === categoryKey,
          }"
        >
          <span class="filters-category__item__text">
            {{ child.name }}
          </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.filters-category {
  &__container {
    max-height: 400px;
    overflow: auto;
  }

  &__item {
    display: inline-flex;
    width: fit-content;
    gap: var(--pl-unit-x1);
    align-items: center;
    cursor: pointer;
    background: none;
    margin: 0;
    border-radius: var(--pl-unit-x2);
    padding: var(--pl-unit-x2);
    white-space: normal;
    font: var(--pl-font-body-large-regular);

    &:hover {
      color: var(--u-filters-category-color-hover);
    }

    &&_selected {
      background: var(--u-filters-category-background-selected);
      border-radius: var(--pl-unit-x2);
      padding: var(--pl-unit-x2);
      margin: 0 var(--pl-unit-x2) 0 var(--pl-unit-x5);
      color: var(--u-filters-category-color-selected);
      font: var(--pl-font-body-large-accent);
    }

    .filters-category__children:not(&_selected) & {
      margin-left: var(--pl-unit-x2);
    }

    &__arrow {
      display: flex;
      align-items: center;

      .filters-category__item_selected & {
        display: none;
      }
    }
  }

  &__children,
  &__parents {
    display: flex;
    flex-direction: column;
  }

  &__children {
    padding-left: var(--pl-unit-x5);
  }
}
</style>
